import api from "./api";

const documentoService = {
  /**
   * Criar um novo documento em uma solicitação
   * @param {number} solicitacaoDeLicencaId - ID da solicitação de licença
   * @param {string} fileName - Nome do arquivo
   * @param {string} descricao - Descrição do documento (opcional)
   * @returns {Promise} Response do servidor
   */
  criarDocumento: async (solicitacaoDeLicencaId, fileName, descricao = "") => {
    try {
      const response = await api.post(
        `/Documento/AdicionarDocumentoAUmaSolicitacao`,
        {
          solicitacaoDeLicencaId,
          fileName: String(fileName),
          descricao: String(descricao),
        },
      );
      return response.data;
    } catch (error) {
      console.error("Erro ao criar documento:", error);
      throw error;
    }
  },

  /**
   * Obter documentos por ID de solicitação
   * @param {number} idSolicitacao - ID da solicitação
   * @returns {Promise} Lista de documentos
   */
  obterDocumentosPorSolicitacao: async (idSolicitacao) => {
    try {
      const response = await api.get(`/documento/solicitacao/${idSolicitacao}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Obter todos os documentos
   * @returns {Promise} Lista de todos os documentos
   */
  obterTodosDocumentos: async () => {
    try {
      const response = await api.get("/Documento");
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Obter documento por ID
   * @param {number} id - ID do documento
   * @returns {Promise} Dados do documento
   */
  obterDocumentoPorId: async (id) => {
    try {
      const response = await api.get(`/Documento/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  obterArquivo: async (id) => {
    const response = await api.get(`/Documento/${id}/arquivo`, {
      responseType: "blob",
    });

    const contentType =
      response.headers?.["content-type"] || "application/octet-stream";
    const contentDisposition = response.headers?.["content-disposition"] || "";
    const fileNameMatch = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(
      contentDisposition,
    );
    const fileName = decodeURIComponent(
      fileNameMatch?.[1] || fileNameMatch?.[2] || "documento",
    );

    return {
      blob: new Blob([response.data], { type: contentType }),
      fileName,
      contentType,
    };
  },

  /**
   * Atualizar documento
   * @param {Object} payload - Dados do documento a atualizar
   * @param {number} payload.id - ID do documento
   * @param {string} payload.descricao - Descrição do documento
   * @param {boolean} payload.obrigatorio - Se o documento é obrigatório
   * @param {string} payload.observacoes - Observações adicionais (opcional)
   * @returns {Promise} Response do servidor
   */
  atualizarDocumento: async (payload) => {
    try {
      const response = await api.put("/Documento", payload);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Upload de arquivo para documento
   * @param {Object} payload - Dados do upload
   * @param {number} payload.documentoId - ID do documento
   * @param {File} payload.arquivo - Arquivo a ser enviado
   * @returns {Promise} Response do servidor
   */
  uploadArquivo: async (payload) => {
    try {
      const formData = new FormData();
      formData.append("documentoId", payload.documentoId);
      formData.append("arquivo", payload.arquivo);

      const response = await api.post("/Documento/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  criarDocumentoProtocolo: async (protocoloId, fileName, descricao = "") => {
    const response = await api.post("/Documento/AdicionarDocumentoAProtocolo", {
      protocoloId,
      fileName: String(fileName),
      descricao: String(descricao),
    });
    return response.data;
  },

  obterDocumentosPorProtocolo: async (protocoloId) => {
    const response = await api.get(`/Documento/protocolo/${protocoloId}`);
    return response.data;
  },

  uploadDocumentoProtocolo: async (
    protocoloId,
    documentoId,
    arquivo,
    removerOpenAction = false,
  ) => {
    const formData = new FormData();
    formData.append("protocoloId", protocoloId);
    formData.append("documentoId", documentoId);
    formData.append("arquivo", arquivo);
    if (removerOpenAction) {
      formData.append("removerOpenAction", "true");
    }

    const response = await api.post(
      "/Documento/UploadDeDocumentoSolicitadoProtocolo",
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      },
    );
    return response.data;
  },
};

export default documentoService;
